import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { RbacTableColumn } from './rbac-table-column.model';
import { ApiCallsService } from '../../services/api-calls.service'

@Component({
  selector: 'app-rbac-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rbac-table.component.html',
  styleUrls: ['./rbac-table.component.scss']
})
export class RbacTableComponent implements OnChanges {
  @Input() columns: RbacTableColumn[] = [];
  @Input() rows: Record<string, any>[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No records found.';
  @Input() showActions = true;
  @Input() showPermission = false;
  @Input() selectedRowId: string | null = null;

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() toggleStatus = new EventEmitter<any>();
  @Output() rowSelect = new EventEmitter<any>();
  @Output() permission = new EventEmitter<any>();

  trackByRow = (_: number, row: Record<string, any>) => row['id'] ?? _;

  // internal cache: category_type -> category name
  private categoryMap: Record<string, string> | null = null;

  constructor(private configurationService: ApiCallsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // only fetch once, and only if this table actually has a 'category' column
    if (!this.categoryMap && this.columns.some(c => c.key === 'category')) {
      this.loadCategoryMap();
    }
  }

  private loadCategoryMap(): void {
    this.categoryMap = {}; // set immediately so we don't fire the request twice while it's in flight
    this.configurationService.getCategories().subscribe({
      next: (res) => {
        const categories = res?.Data ?? res ?? [];
        const map: Record<string, string> = {};
        categories.forEach((c: any) => {
          map[String(c.category_type)] = c.name;
        });
        this.categoryMap = map;
      },
      error: () => {
        this.categoryMap = {}; // fail safe — falls back to raw values below
      }
    });
  }

  /** Resolves a cell's display value. Currently only 'category' is mapped; everything else passes through untouched. */
/** Resolves a cell's display value. Currently only 'category' is mapped; everything else passes through untouched. */
  getCellValue(row: Record<string, any>, col: RbacTableColumn): any {
    const raw = row[col.key];
    if (col.key === 'category' && this.categoryMap) {
      return this.categoryMap[String(raw)] ?? raw;
    }
    return raw;
  }

  /** Rows with sub_module = 'N' have no inner modules to drill into, so navigation is blocked. */
  isRowBlocked(row: Record<string, any>): boolean {
    return row['sub_module'] === 'N';
  }

  onRowClick(row: Record<string, any>): void {
    if (this.isRowBlocked(row)) return;
    this.rowSelect.emit(row);
  }
}
